import { useState, useEffect } from "react";
import { RefreshCw, Download, Eye, Filter, Trash2 } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../components/Toast";
import { fmtDateTime } from "../utils/helpers";
import Card from "../components/Card";
import StatusBadge from "../components/StatusBadge";
import Modal from "../components/Modal";
import Pagination from "../components/Pagination";
import type { Lead, Form } from "../types";

export default function LeadsPage() {
  const { toast } = useToast();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filterFormId, setFilterFormId] = useState<string>("");
  const [filterFrom, setFilterFrom] = useState<string>("");
  const [filterTo, setFilterTo] = useState<string>("");

  // Pagination
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Detail Modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [activeLead, setActiveLead] = useState<Lead | null>(null);
  const [activeLeadJobs, setActiveLeadJobs] = useState<any[]>([]);

  const fetchForms = async () => {
    try {
      const res = await api.get("/forms");
      if (res.data.ok) setForms(res.data.forms || []);
    } catch {}
  };

  const fetchLeads = async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", p.toString());
      if (filterFormId) params.append("form_id", filterFormId);
      if (filterFrom) params.append("from", filterFrom);
      if (filterTo) params.append("to", filterTo);

      const res = await api.get(`/leads?${params.toString()}`);
      if (res.data.ok) {
        setLeads(res.data.leads || []);
        setHasMore((res.data.leads || []).length === res.data.limit);
        setPage(p);
      }
    } catch (err: any) {
      toast(err.response?.data?.error || "Failed to load leads", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
  }, []);

  useEffect(() => {
    fetchLeads(1);
  }, [filterFormId, filterFrom, filterTo]);

  const handleExport = async () => {
    const params = new URLSearchParams();
    if (filterFormId) params.append("form_id", filterFormId);
    if (filterFrom) params.append("from", filterFrom);
    if (filterTo) params.append("to", filterTo);
    
    try {
      const token = localStorage.getItem("wa_token");
      const res = await fetch(`/api/leads/export?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        toast("Export failed", "error");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "leads.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast("Export failed", "error");
    }
  };

  const openDetail = async (lead: Lead) => {
    try {
      const res = await api.get(`/leads/${lead.id}`);
      if (res.data.ok) {
        setActiveLead(res.data.lead);
        setActiveLeadJobs(res.data.automation_jobs || []);
        setDetailModalOpen(true);
      }
    } catch (err: any) {
      toast(err.response?.data?.error || "Failed to load lead details", "error");
    }
  };

  
  const deleteLead = async (id: number) => {
    if (!confirm("Eliminare questo lead? L'azione è irreversibile.")) return;
    try {
      const res = await api.delete(`/leads/${id}`);
      if (res.data.ok) {
        toast("Lead eliminato", "success");
        fetchLeads(page);
      }
    } catch (err: any) {
      toast(err.response?.data?.error || "Errore", "error");
    }
  };

return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Leads</h1>
          <p className="text-zinc-400 text-sm mt-1">View and export form submissions</p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl font-medium transition-colors"
        >
          <Download size={18} /> Export Excel
        </button>
      </div>

      <Card title="Filters">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm text-zinc-400 mb-1">Filter by Form</label>
            <select
              value={filterFormId}
              onChange={(e) => setFilterFormId(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-fuchsia-500"
            >
              <option value="">All Forms</option>
              {forms.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-1">From Date</label>
            <input
              type="date"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
              className="bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-fuchsia-500"
            />
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-1">To Date</label>
            <input
              type="date"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
              className="bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-fuchsia-500"
            />
          </div>
          <button
            onClick={() => fetchLeads(1)}
            disabled={loading}
            className="px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl transition-colors flex items-center gap-2"
          >
            <Filter size={16} /> Apply
          </button>
        </div>
      </Card>

      <Card
        title="Submissions"
        actions={
          <button onClick={() => fetchLeads(page)} disabled={loading} className="text-zinc-400 hover:text-white transition-colors">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-zinc-500 text-left border-b border-zinc-800">
                <th className="pb-3 font-medium">ID</th>
                <th className="pb-3 font-medium">Form</th>
                <th className="pb-3 font-medium">Name</th>
                <th className="pb-3 font-medium">Phone</th>
                <th className="pb-3 font-medium">Automation</th>
                <th className="pb-3 font-medium">Date</th>
                <th className="pb-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-zinc-600 py-8">
                    No leads found
                  </td>
                </tr>
              ) : (
                leads.map((l) => (
                  <tr key={l.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="py-3 text-zinc-400">{l.id}</td>
                    <td className="py-3 text-zinc-300">{l.form_name}</td>
                    <td className="py-3 font-medium text-white">{l.submitter_name || "—"}</td>
                    <td className="py-3 text-zinc-300 font-mono text-xs">{l.phone || "—"}</td>
                    <td className="py-3">
                      {l.automation_status ? (
                        <StatusBadge status={l.automation_status} />
                      ) : (
                        <span className="text-xs text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="py-3 text-zinc-400">{fmtDateTime(l.created_at)}</td>
                    <td className="py-3 text-right">
                      <button
                        onClick={() => openDetail(l)}
                        className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => deleteLead(l.id)}
                        className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-zinc-800 rounded-lg transition-colors"
                        title="Elimina"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <div className="mt-4 flex justify-between items-center">
          <button
            onClick={() => fetchLeads(page - 1)}
            disabled={page === 1 || loading}
            className="px-3 py-1.5 text-sm bg-zinc-800 text-zinc-300 rounded-lg disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-zinc-500">Page {page}</span>
          <button
            onClick={() => fetchLeads(page + 1)}
            disabled={!hasMore || loading}
            className="px-3 py-1.5 text-sm bg-zinc-800 text-zinc-300 rounded-lg disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </Card>

      {/* Detail Modal */}
      <Modal open={detailModalOpen} onClose={() => setDetailModalOpen(false)} title={`Lead #${activeLead?.id}`} wide>
        {activeLead && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-white mb-3 border-b border-zinc-800 pb-2">Submission Data</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">Form:</span>
                    <span className="text-white text-sm">{activeLead.form_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">Date:</span>
                    <span className="text-white text-sm">{fmtDateTime(activeLead.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">Name:</span>
                    <span className="text-white text-sm">{activeLead.submitter_name || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">Phone:</span>
                    <span className="text-white text-sm font-mono">{activeLead.phone || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">Email:</span>
                    <span className="text-white text-sm">{activeLead.email || "—"}</span>
                  </div>
                </div>

                <h3 className="text-sm font-medium text-white mt-6 mb-3 border-b border-zinc-800 pb-2">Custom Fields</h3>
                {Object.keys(activeLead.payload).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(activeLead.payload).map(([key, value]) => (
                      <div key={key} className="flex flex-col">
                        <span className="text-zinc-500 text-xs font-mono">{key}</span>
                        <span className="text-white text-sm bg-zinc-900 p-2 rounded-lg mt-1">
                          {String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500">No custom fields</p>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium text-white mb-3 border-b border-zinc-800 pb-2">Automation Jobs</h3>
                {activeLeadJobs.length === 0 ? (
                  <p className="text-sm text-zinc-500">No automation jobs triggered</p>
                ) : (
                  <div className="space-y-3">
                    {activeLeadJobs.map((job) => (
                      <div key={job.id} className="bg-zinc-900 p-3 rounded-xl border border-zinc-800">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs text-zinc-400">Job #{job.id}</span>
                          <StatusBadge status={job.status} />
                        </div>
                        <div className="text-xs text-zinc-400 flex justify-between">
                          <span>Attempts: {job.attempts}</span>
                          {job.sent_at && <span>Sent: {fmtDateTime(job.sent_at)}</span>}
                        </div>
                        {job.last_error && (
                          <div className="mt-2 text-xs text-red-400 bg-red-500/10 p-2 rounded">
                            {job.last_error}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
